import { useCallback, useEffect, useState } from 'react';

import { listDealPhotos, type DealPhoto } from '@/lib/db/admin/deal-photos';
import { getAdminDealById, type AdminDeal } from '@/lib/db/admin/deals';

export type AdminDealState =
  | { status: 'loading'; deal: null; error: null }
  | { status: 'success'; deal: AdminDeal; error: null }
  | { status: 'not_found'; deal: null; error: null }
  | { status: 'error'; deal: null; error: Error };

const INITIAL: AdminDealState = { status: 'loading', deal: null, error: null };

/**
 * Load a single deal by id for the admin edit form.
 *
 * Returns four discriminated states:
 *   - loading  → first render, fetch in flight
 *   - success  → row loaded
 *   - not_found → query returned null (id invalid OR RLS hides the row)
 *   - error    → query threw (network / unexpected)
 *
 * Re-fires on `id` changes and supports optimistic refetch via the
 * returned `refetch` setter (used after a transition or save to pick
 * up the updated row + new lifecycle stamps).
 */
export function useAdminDeal(id: string | undefined): AdminDealState & { refetch: () => void } {
  const [state, setState] = useState<AdminDealState>(INITIAL);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!id) {
      setState({ status: 'not_found', deal: null, error: null });
      return;
    }
    let cancelled = false;
    setState(INITIAL);

    getAdminDealById(id)
      .then((deal) => {
        if (cancelled) return;
        if (!deal) {
          setState({ status: 'not_found', deal: null, error: null });
          return;
        }
        setState({ status: 'success', deal, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          deal: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [id, tick]);

  return { ...state, refetch: () => setTick((n) => n + 1) };
}

export type DealPhotosState =
  | { status: 'loading'; photos: DealPhoto[] }
  | { status: 'ready'; photos: DealPhoto[] }
  | { status: 'error'; photos: DealPhoto[]; error: Error };

/**
 * Load the photo set of a deal (BO-3). Holds the array in component state
 * so the uploader / gallery can drive optimistic updates without a refetch
 * round-trip after every mutation. `setPhotos` is exposed for those
 * optimistic paths; `refetch` re-runs the SELECT.
 */
export function useDealPhotos(dealId: string | undefined): DealPhotosState & {
  setPhotos: (updater: DealPhoto[] | ((prev: DealPhoto[]) => DealPhoto[])) => void;
  refetch: () => void;
} {
  const [state, setState] = useState<DealPhotosState>({ status: 'loading', photos: [] });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!dealId) {
      setState({ status: 'ready', photos: [] });
      return;
    }
    let cancelled = false;
    setState((prev) => ({ status: 'loading', photos: prev.photos }));

    listDealPhotos(dealId)
      .then((photos) => {
        if (cancelled) return;
        setState({ status: 'ready', photos });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          photos: [],
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [dealId, tick]);

  const setPhotos = useCallback((updater: DealPhoto[] | ((prev: DealPhoto[]) => DealPhoto[])) => {
    setState(
      (prev) =>
        ({
          ...prev,
          photos: typeof updater === 'function' ? updater(prev.photos) : updater,
        }) as DealPhotosState,
    );
  }, []);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  return { ...state, setPhotos, refetch };
}
